// Soporte al usuario (VLT-328): "Reportar un problema" + tickets con hilo.
// El contexto técnico (ruta, viewport, navegador, idioma) viaja con el reporte
// para diagnosticar sin ping-pong. RLS: cada quien ve lo suyo; el staff de la
// firma gestiona los de su organización; la plataforma ve todo (0009).
import { supabase, hasBackend } from '@/lib/supabase';
import { loadFailed } from './loadError';
import { sessionUser } from '@/lib/sessionUser';

export type TicketCategory = 'app' | 'billing';
export type TicketKind = 'bug' | 'question' | 'suggestion' | 'feature';
export type TicketUrgency = 'low' | 'normal' | 'high';
export type TicketStatus = 'open' | 'answered' | 'waiting_user' | 'resolved' | 'closed';

export interface TicketMessage {
  id: string;
  authorName: string;
  authorRole: string | null;
  mine: boolean;
  body: string;
  createdAt: string;
}

export interface SupportTicket {
  id: string;
  ticketNo: number;
  subject: string;
  category: TicketCategory;
  kind: TicketKind;
  urgency: TicketUrgency;
  area?: string | null;
  status: TicketStatus;
  creatorName: string;
  createdAt: string;
  updatedAt: string;
  messages: TicketMessage[];
  /** VLT-368: adjunto del reporte (key del vault; en demo solo nombre). */
  attachment?: { key?: string; name: string; sizeLabel: string } | null;
}

export interface NewTicketInput {
  category: TicketCategory;
  kind: TicketKind;
  urgency: TicketUrgency;
  subject: string;
  area?: string;
  description: string;
  /** Contexto capturado automáticamente (ruta, viewport, userAgent, idioma). */
  context: Record<string, string>;
  /** VLT-368: adjunto opcional ya subido al vault (key) — en demo solo nombre. */
  attachment?: { key?: string; name: string; sizeLabel: string } | null;
}

/** Estados que cuentan como "esperan por ti" para el usuario que reportó. */
export const WAITING_USER: TicketStatus[] = ['answered', 'waiting_user'];
export const RESOLVED: TicketStatus[] = ['resolved', 'closed'];

/** Conteos para las tarjetas (pura, testeable). */
export function ticketCounts(tickets: { status: TicketStatus }[]): { open: number; waiting: number; resolved: number } {
  return {
    open: tickets.filter((t) => t.status === 'open').length,
    waiting: tickets.filter((t) => WAITING_USER.includes(t.status)).length,
    resolved: tickets.filter((t) => RESOLVED.includes(t.status)).length,
  };
}

export interface SupportService {
  /** Tickets visibles según RLS (los míos; todos los de la org si soy staff). */
  list(): Promise<SupportTicket[]>;
  create(input: NewTicketInput): Promise<{ ok: boolean; ticketNo?: number }>;
  reply(ticketId: string, body: string): Promise<boolean>;
  setStatus(ticketId: string, status: TicketStatus): Promise<boolean>;
}

// ─── Implementación real (Supabase, RLS por tenant) ─────────────────────────

interface Row {
  id: string; ticket_no: number; subject: string; category: TicketCategory;
  kind: TicketKind; urgency: TicketUrgency; area: string | null;
  status: TicketStatus; created_by: string; created_at: string; updated_at: string;
  creator: { full_name: string | null } | null;
  support_messages: {
    id: string; author_id: string; author_role: string | null; body: string; created_at: string;
    author: { full_name: string | null } | null;
  }[];
}

const SELECT = 'id, ticket_no, subject, category, kind, urgency, area, status, context, created_by, created_at, updated_at, creator:profiles!created_by(full_name), support_messages(id, author_id, author_role, body, created_at, author:profiles!author_id(full_name))';

const isAttachment = (v: unknown): v is { key?: string; name: string; sizeLabel: string } =>
  !!v && typeof v === 'object' && 'name' in v;

const realSupport: SupportService = {
  async list() {
    const { data: auth } = await sessionUser();
    const me = auth.user?.id;
    const { data, error } = await supabase
      .from('support_tickets')
      .select(SELECT)
      .order('updated_at', { ascending: false })
      .limit(200);
    if (error) loadFailed('support.list', error);
    return (data as unknown as Row[]).map((r) => ({
      id: r.id, ticketNo: r.ticket_no, subject: r.subject, category: r.category,
      kind: r.kind, urgency: r.urgency, area: r.area, status: r.status,
      creatorName: r.creator?.full_name ?? '—',
      createdAt: r.created_at, updatedAt: r.updated_at,
      attachment: (() => {
        const a = (r as unknown as { context?: Record<string, unknown> }).context?.attachment;
        return isAttachment(a) ? a : null;
      })(),
      messages: [...r.support_messages]
        .sort((a, b) => a.created_at.localeCompare(b.created_at))
        .map((m) => ({
          id: m.id, authorName: m.author?.full_name ?? '—', authorRole: m.author_role,
          mine: m.author_id === me, body: m.body, createdAt: m.created_at,
        })),
    }));
  },

  async create(input) {
    const { data: auth } = await sessionUser();
    if (!auth.user) return { ok: false };
    const { data: prof } = await supabase.from('profiles').select('organization_id, role').eq('id', auth.user.id).maybeSingle();
    const orgId = (prof as { organization_id: string; role: string } | null)?.organization_id;
    if (!orgId) return { ok: false };
    const { data, error } = await supabase
      .from('support_tickets')
      .insert({
        organization_id: orgId, created_by: auth.user.id,
        category: input.category, kind: input.kind, urgency: input.urgency,
        subject: input.subject, area: input.area ?? null,
        context: input.attachment ? { ...input.context, attachment: input.attachment } : input.context,
      } as never)
      .select('id, ticket_no')
      .single();
    if (error || !data) {
      if (error) console.warn('[VaulTex] support.create:', error.message);
      return { ok: false };
    }
    const row = data as { id: string; ticket_no: number };
    // La descripción es el primer mensaje del hilo.
    const { error: e2 } = await supabase.from('support_messages').insert({
      organization_id: orgId, ticket_id: row.id, author_id: auth.user.id,
      author_role: (prof as { role: string } | null)?.role ?? null, body: input.description,
    } as never);
    if (e2) console.warn('[VaulTex] support.firstMessage:', e2.message);
    return { ok: true, ticketNo: row.ticket_no };
  },

  async reply(ticketId, body) {
    const { data: auth } = await sessionUser();
    if (!auth.user) return false;
    const { data: prof } = await supabase.from('profiles').select('organization_id, role').eq('id', auth.user.id).maybeSingle();
    const p = prof as { organization_id: string; role: string } | null;
    if (!p) return false;
    const { error } = await supabase.from('support_messages').insert({
      organization_id: p.organization_id, ticket_id: ticketId,
      author_id: auth.user.id, author_role: p.role, body,
    } as never);
    if (error) {
      console.warn('[VaulTex] support.reply:', error.message);
      return false;
    }
    // El staff que responde deja el ticket "contestado"; el usuario que
    // responde lo reabre a "open" (el equipo debe volver a mirarlo).
    const staffRoles = ['super_admin', 'admin', 'tax_preparer', 'notary', 'support'];
    const next: TicketStatus = staffRoles.includes(p.role) ? 'answered' : 'open';
    await supabase.from('support_tickets').update({ status: next } as never).eq('id', ticketId);
    return true;
  },

  async setStatus(ticketId, status) {
    const { error } = await supabase.from('support_tickets').update({ status } as never).eq('id', ticketId);
    if (error) console.warn('[VaulTex] support.setStatus:', error.message);
    return !error;
  },
};

// ─── Implementación demo (fixtures) ─────────────────────────────────────────

const DEMO: SupportTicket[] = [
  {
    id: 'tk1', ticketNo: 25, subject: 'La lista de documentos no carga en mi teléfono',
    category: 'app', kind: 'bug', urgency: 'normal', area: '/documents', status: 'answered',
    creatorName: 'María Santos', createdAt: '2026-09-02T14:15:00Z', updatedAt: '2026-09-02T18:19:00Z',
    messages: [
      { id: 'm1', authorName: 'María Santos', authorRole: 'client', mine: true, body: 'Cuando abro Documentos desde mi teléfono se queda cargando y no muestra nada. En la computadora sí funciona.', createdAt: '2026-09-02T14:15:00Z' },
      { id: 'm2', authorName: 'Equipo VaulTex', authorRole: 'support', mine: false, body: 'Gracias por avisar, María. Con tu reporte llegó el detalle de tu navegador y ya reprodujimos el problema: pasa en pantallas angostas con más de 20 documentos. Lo corregimos y sale en la próxima actualización (hoy mismo). ¿Puedes probar de nuevo esta tarde y confirmarnos?', createdAt: '2026-09-02T18:19:00Z' },
    ],
  },
  {
    id: 'tk2', ticketNo: 24, subject: '¿Puedo cambiar el idioma de mi portal?',
    category: 'app', kind: 'question', urgency: 'low', area: '/', status: 'resolved',
    creatorName: 'María Santos', createdAt: '2026-08-28T10:00:00Z', updatedAt: '2026-08-28T10:40:00Z',
    messages: [
      { id: 'm3', authorName: 'María Santos', authorRole: 'client', mine: true, body: '¿Cómo cambio el idioma a inglés?', createdAt: '2026-08-28T10:00:00Z' },
      { id: 'm4', authorName: 'Equipo VaulTex', authorRole: 'support', mine: false, body: 'Arriba a la derecha tienes el selector ES/EN. Tu preferencia queda guardada para las próximas visitas.', createdAt: '2026-08-28T10:40:00Z' },
    ],
  },
];

const fixtureSupport: SupportService = {
  list: () => Promise.resolve(structuredClone(DEMO)),
  create: (input) => {
    const no = Math.max(...DEMO.map((t) => t.ticketNo)) + 1;
    DEMO.unshift({
      id: `tk${no}`, ticketNo: no, subject: input.subject, category: input.category,
      kind: input.kind, urgency: input.urgency, area: input.area, status: 'open',
      creatorName: 'Tú', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
      attachment: input.attachment ?? null,
      messages: [{ id: `m-${no}`, authorName: 'Tú', authorRole: null, mine: true, body: input.description, createdAt: new Date().toISOString() }],
    });
    return Promise.resolve({ ok: true, ticketNo: no });
  },
  reply: (ticketId, body) => {
    const t = DEMO.find((x) => x.id === ticketId);
    if (t) {
      t.messages.push({ id: `m${Date.now()}`, authorName: 'Tú', authorRole: null, mine: true, body, createdAt: new Date().toISOString() });
      t.status = 'open';
      t.updatedAt = new Date().toISOString();
    }
    return Promise.resolve(true);
  },
  setStatus: (ticketId, status) => {
    const t = DEMO.find((x) => x.id === ticketId);
    if (t) t.status = status;
    return Promise.resolve(true);
  },
};

export const supportService: SupportService = hasBackend ? realSupport : fixtureSupport;
